import { Box, IconButton, Stack, Typography } from "@mui/material";
import React from "react";
import { OpenConfirmOptions } from "@/components/providers/confirmProvider";
import { VIOLET } from "@/config/mui/colorPalette";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import { CrossIcon } from "../icons";
import ConfirmModal from "./confirmModal";
import SlideBottomDialog from "./slideBottomDialog";

type DrawerProps = Parameters<typeof SlideBottomDialog>[0];
type ModalProps = Parameters<typeof ConfirmModal>[0];

type ModalHeadlineProps = {
  title: string;
  onClose: () => void;
  titleAlign?: "left" | "center";
};

type ResponsiveConfirmDialogProps = ModalHeadlineProps & {
  isOpen: boolean;
  onSubmit: () => void;
  children: React.ReactNode;
  submitText?: string;
  isConfirmDisabled?: boolean;
  drawerProps?: Partial<DrawerProps>;
  modalProps?: Partial<ModalProps>;
  modalConfirmOptions?: Partial<OpenConfirmOptions>;
};

const ModalHeadline = ({ title, onClose, titleAlign }: ModalHeadlineProps) => (
  <Stack
    direction="row"
    sx={{
      height: "36px",
      justifyContent: "space-between",
      alignItems: "center",
      textAlign: titleAlign,
    }}
  >
    {titleAlign === "center" && <Box width="36px" height="36px" />}
    <Typography variant="h3" flex={1}>
      {title}
    </Typography>
    <IconButton
      sx={{ color: VIOLET[90], width: "36px", height: "36px" }}
      onClick={onClose}
    >
      <CrossIcon />
    </IconButton>
  </Stack>
);

export const ResponsiveConfirmDialog = ({
  isOpen,
  title,
  onClose,
  onSubmit,
  submitText = "Save",
  titleAlign = "center",
  isConfirmDisabled = false,
  children,
  drawerProps,
  modalProps,
  modalConfirmOptions = {},
  ...props
}: ResponsiveConfirmDialogProps) => {
  const isMobile = useMoxieMediaQuery("ipad-ver", "down");

  // TODO: remove this once the ConfirmModal is updated to use MUI Dialog (dev-5277)
  const wrappedChildren = <Box sx={{ pt: 2, pb: 3 }}>{children}</Box>;

  if (isMobile) {
    return (
      <SlideBottomDialog
        open={isOpen}
        title={title}
        onClose={onClose}
        onSubmit={onSubmit}
        submitButtonText={submitText}
        isDisabled={isConfirmDisabled}
        withCancelButton
        showDivider
        crossIconColor={VIOLET[90]}
        {...drawerProps}
        {...props}
      >
        {children}
      </SlideBottomDialog>
    );
  }

  return (
    <ConfirmModal
      isConfirmOpen={isOpen}
      confirm={onSubmit}
      discard={onClose}
      confirmOptions={{
        title: (
          <ModalHeadline
            title={title}
            onClose={onClose}
            titleAlign={titleAlign}
          />
        ),
        description: wrappedChildren,
        confirmButtonText: submitText,
        confirmButtonDisabled: isConfirmDisabled,
        discardButtonText: "Cancel",
        ...modalConfirmOptions,
      }}
      {...modalProps}
      {...props}
    />
  );
};
